import { Button } from "../ui/button";
import { Check, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";

export function ProgramSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({ projects: 0, success: 0, experts: 0, funding: 0 });
  const sectionRef = useRef<HTMLElement>(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Animate counters
          const duration = 2000;
          const steps = 60;
          const interval = duration / steps;

          let step = 0;
          const timer = setInterval(() => {
            step++;
            const progress = step / steps;
            setCounters({
              projects: Math.round(100 * progress),
              success: Math.round(92 * progress),
              experts: Math.round(25 * progress),
              funding: Math.round(50 * progress),
            });
            if (step >= steps) clearInterval(timer);
          }, interval);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Define program phases with translations
  const programPhases = [
    {
      phase: t('public.program.phase1.phase'),
      title: t('public.program.phase1.title'),
      items: [
        t('public.program.phase1.items.0'),
        t('public.program.phase1.items.1'),
        t('public.program.phase1.items.2'),
      ],
    },
    {
      phase: t('public.program.phase2.phase'),
      title: t('public.program.phase2.title'),
      items: [
        t('public.program.phase2.items.0'),
        t('public.program.phase2.items.1'),
        t('public.program.phase2.items.2'),
      ],
    },
    {
      phase: t('public.program.phase3.phase'),
      title: t('public.program.phase3.title'),
      items: [
        t('public.program.phase3.items.0'),
        t('public.program.phase3.items.1'),
        t('public.program.phase3.items.2'),
      ],
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-20 overflow-hidden" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Background Split */}
      <div className="absolute inset-0 z-0">
        <div className="h-1/2 w-full bg-background" />
        <div className="h-1/2 w-full bg-hero-gradient" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground">
            {t('public.program.subtitle')}
          </h2>
        </div>

        {/* Phases Grid */}
        <div className="relative mt-20">
          {/* Connecting Line (Desktop) */}
          <div className="hidden md:block absolute top-[90px] left-0 w-full h-8 z-0 pointer-events-none">
            <svg width="100%" height="60" viewBox="0 0 1000 60" fill="none" preserveAspectRatio="none">
              <path
                d="M0 30C100 30 150 5 250 5C350 5 400 55 500 55C600 55 650 5 750 5C850 5 900 30 1000 30"
                stroke="hsl(173 78% 40% / 0.3)"
                strokeWidth="4"
                strokeDasharray="12 8"
                className={isVisible ? "animate-draw-line" : ""}
              />
            </svg>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {programPhases.map((phase, index) => (
              <div
                key={phase.phase}
                className={`flex flex-col items-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                {/* Phase Circle */}
                <div className="relative mb-12 group">
                  <div className="w-40 h-40 sm:w-48 sm:h-48 rounded-full bg-background border-4 border-secondary shadow-lg flex flex-col items-center justify-center p-6 text-center z-10 relative group-hover:scale-105 transition-transform duration-500">
                    <span className="text-secondary font-bold text-lg sm:text-xl mb-1">{phase.phase}:</span>
                    <span className="text-foreground font-bold text-xl sm:text-2xl leading-tight">{phase.title}</span>
                  </div>
                  {/* Decorative pulse */}
                  <div className="absolute inset-0 rounded-full bg-secondary/20 animate-ping-slow -z-0" />

                  {/* Phase dot on the line */}
                  <div className="hidden md:block absolute top-[90px] -right-4 w-3 h-3 bg-secondary rounded-full" />
                </div>

                {/* Details (Dark Section) */}
                <div className="mt-8 text-white w-full max-w-xs md:max-w-none">
                  <h4 className="text-xl font-bold mb-6 text-center md:text-start border-b border-primary-foreground/10 pb-2">
                    {phase.title}
                  </h4>
                  <ul className="space-y-4">
                    {phase.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-3">
                        <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-secondary/20 flex items-center justify-center">
                          <Check className="w-3 h-3 text-secondary" />
                        </div>
                        <span className="text-primary-foreground/80 text-sm leading-relaxed">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Action */}
        <div className={`mt-20 text-center transition-all duration-700 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Button
            asChild
            size="lg"
            variant="secondary"
            className="bg-primary-foreground text-primary hover:bg-white gap-3 group px-10 py-7 rounded-full text-lg font-bold shadow-xl hover-lift"
          >
            <Link to="/program">
              {t('public.program.discover')}
              <ArrowLeft className="w-6 h-6 transition-transform group-hover:-translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes draw-line {
          from { stroke-dashoffset: 1000; }
          to { stroke-dashoffset: 0; }
        }
        .animate-draw-line {
          stroke-dasharray: 1000;
          stroke-dashoffset: 1000;
          animation: draw-line 3s ease-out forwards;
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.2); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }
        .animate-ping-slow {
          animation: ping-slow 3s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
    </section>
  );
}